import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShellLayout } from '@/components/templates/AppShellLayout';
import { PublicDirectoryLayout } from '@/components/templates/PublicDirectoryLayout';
import { DisabledView } from '@/components/organisms/DisabledView';
import { FeatureGate } from '@/components/molecules/FeatureGate';
import { RoleGate } from '@/components/molecules/RoleGate';
import { RequireAuth } from '@/components/molecules/RequireAuth';
import { EmptyState } from '@/components/molecules/EmptyState';
import { BetaRouteGate } from '@/app/BetaRouteGate';
import type { FeatureId } from '@/data/featuresRegistry';
import type { RoleId } from '@/data/roles';
import type { AuthUser } from '@/stores/auth';

const wrap = (loader: () => Promise<{ default: ComponentType }>): ReactNode => {
  const C = lazy(loader);
  return (
    <Suspense fallback={<div style={{ padding: 60, color: 'var(--ink-3)' }}>Carregando…</div>}>
      <C />
    </Suspense>
  );
};

const gate = (feature: FeatureId, node: ReactNode): ReactNode => (
  <FeatureGate feature={feature} fallback={<DisabledView feature={feature} />}>
    {node}
  </FeatureGate>
);

const roleGate = (
  allow: RoleId[],
  node: ReactNode,
  associationRoles?: NonNullable<AuthUser['association_role']>[],
): ReactNode => (
  <RoleGate
    allow={allow}
    associationRoles={associationRoles}
    fallback={
      <EmptyState
        icon="lock"
        title="Acesso restrito"
        description="Seu perfil não tem permissão para acessar esta área. Entre com uma conta autorizada ou peça acesso ao responsável."
      />
    }
  >
    {node}
  </RoleGate>
);

export const router = createBrowserRouter([
  {
    path: '/',
    children: [
      { index: true, element: wrap(() => import('@/features/site/SitePage')) },
      { path: 'site', element: <Navigate to="/" replace /> },
      { path: 'waitlist', element: wrap(() => import('@/features/waitlist/WaitlistPage')) },
      { path: 'legal', element: wrap(() => import('@/features/legal/LegalPage')) },
      { path: 'status', element: wrap(() => import('@/features/status/StatusPage')) },
      { path: 'auth/login', element: wrap(() => import('@/features/auth/LoginPage')) },
      { path: 'auth/signup', element: wrap(() => import('@/features/auth/SignupPage')) },
      { path: 'auth/forgot', element: wrap(() => import('@/features/auth/ForgotPasswordPage')) },
      { path: 'auth/password/edit', element: wrap(() => import('@/features/auth/ResetPasswordPage')) },
      {
        element: <PublicDirectoryLayout />,
        children: [
          { path: 'associations', element: wrap(() => import('@/features/associations/AssociationsPage')) },
          { path: 'a/:id', element: wrap(() => import('@/features/association/AssociationPage')) },
          { path: 'strains', element: wrap(() => import('@/features/strains/StrainsPage')) },
          { path: 'strains/:id', element: wrap(() => import('@/features/strains/StrainDetailPage')) },
          { path: 'effects', element: wrap(() => import('@/features/effects/EffectsPage')) },
          { path: 'communities', element: gate('communities', wrap(() => import('@/features/communities/CommunitiesPage'))) },
          { path: 'c/:id', element: gate('communities', wrap(() => import('@/features/community/CommunityPage'))) },
        ],
      },
      {
        path: 'onboarding',
        element: <RequireAuth>{wrap(() => import('@/features/onboarding/OnboardingPage'))}</RequireAuth>,
      },
      {
        element: (
          <RequireAuth>
            <BetaRouteGate>
              <AppShellLayout />
            </BetaRouteGate>
          </RequireAuth>
        ),
        children: [
      { path: 'dashboard', element: wrap(() => import('@/features/dashboard/DashboardPage')), handle: { crumb: 'Início' } },
      { path: 'panel', element: roleGate(['assoc'], wrap(() => import('@/features/panel/PanelPage'))), handle: { crumb: 'Painel' } },
      { path: 'panel/plan', element: roleGate(['assoc'], wrap(() => import('@/features/panel/PlanPage'))), handle: { crumb: 'Plano' } },
      { path: 'admin', element: roleGate(['admin'], wrap(() => import('@/features/admin/AdminPage'))), handle: { crumb: 'Visão geral' } },
      { path: 'admin/features', element: roleGate(['admin'], wrap(() => import('@/features/admin/FeaturesAdminPage'))), handle: { crumb: 'Recursos & módulos' } },
      { path: 'admin/waitlist', element: roleGate(['admin'], wrap(() => import('@/features/admin/WaitlistAdminPage'))), handle: { crumb: 'Lista de espera' } },
      { path: 'admin/complaints', element: roleGate(['admin'], wrap(() => import('@/features/admin/ComplaintsPage'))), handle: { crumb: 'Denúncias' } },
      { path: 'admin/legal', element: wrap(() => import('@/features/legal/LegalPage')), handle: { crumb: 'Termos & legal' } },
      {
        path: 'feed',
        element: gate('feed', wrap(() => import('@/features/feed/FeedPage'))),
        handle: { crumb: 'Feed' },
      },
      {
        path: 'messages',
        element: gate('messages', wrap(() => import('@/features/messages/MessagesPage'))),
        handle: { crumb: 'Mensagens', fullBleed: true },
      },
      {
        path: 'notifications',
        element: gate('notifications', wrap(() => import('@/features/notifications/NotificationsPage'))),
        handle: { crumb: 'Notificações' },
      },
      {
        path: 'reels',
        element: gate('reels', wrap(() => import('@/features/reels/ReelsPage'))),
        handle: { crumb: 'Reels & lives' },
      },
      {
        path: 'explore',
        element: gate('explore', wrap(() => import('@/features/discovery/ExplorePage'))),
        handle: { crumb: 'Explorar' },
      },
      {
        path: 'search',
        element: wrap(() => import('@/features/discovery/SearchPage')),
        handle: { crumb: 'Busca' },
      },
      {
        path: 'strains/compare',
        element: gate('strains', wrap(() => import('@/features/strains/CompareStrainsPage'))),
        handle: { crumb: 'Comparar strains' },
      },
      {
        path: 'marketplace',
        element: gate('marketplace', wrap(() => import('@/features/marketplace/MarketplacePage'))),
        handle: { crumb: 'Marketplace' },
      },
      {
        path: 'marketplace/cart',
        element: gate('marketplace', wrap(() => import('@/features/marketplace/CartPage'))),
        handle: { crumb: 'Carrinho' },
      },
      {
        path: 'marketplace/publish',
        element: roleGate(['assoc'], gate('marketplace', wrap(() => import('@/features/marketplace/PublishProductPage')))),
        handle: { crumb: 'Publicar produto' },
      },
      { path: 'diary', element: wrap(() => import('@/features/diary/DiaryPage')), handle: { crumb: 'Diário' } },
      { path: 'meds', element: wrap(() => import('@/features/meds/MedsPage')), handle: { crumb: 'Medicamentos' } },
      {
        path: 'donations',
        element: gate('donations', wrap(() => import('@/features/donations/DonationsPage'))),
        handle: { crumb: 'Fundo solidário' },
      },
      { path: 'get-laudo', element: wrap(() => import('@/features/get-laudo/GetLaudoPage')), handle: { crumb: 'Como ter acesso' } },
      { path: 'account', element: wrap(() => import('@/features/account/AccountPage')), handle: { crumb: 'Minha conta' } },
      { path: 'account/documents', element: wrap(() => import('@/features/account/VaultPage')), handle: { crumb: 'Documentos pessoais' } },
      { path: 'account/edit', element: wrap(() => import('@/features/account/ProfileEditPage')), handle: { crumb: 'Editar perfil' } },
      { path: 'account/recommendations', element: wrap(() => import('@/features/account/RecommendationsPage')), handle: { crumb: 'Recomendações' } },
      { path: 'account/cohort', element: wrap(() => import('@/features/account/CohortPage')), handle: { crumb: 'Coorte' } },
      { path: 'report', element: wrap(() => import('@/features/report/ReportPage')), handle: { crumb: 'Denunciar' } },
      { path: 'claim', element: roleGate(['assoc'], wrap(() => import('@/features/claim/ClaimPage')), ['owner']), handle: { crumb: 'Verificação' } },
      { path: 'manage/team', element: roleGate(['assoc'], wrap(() => import('@/features/manage/TeamPage')), ['owner', 'admin']), handle: { crumb: 'Equipe' } },
      { path: 'manage/docs', element: roleGate(['assoc'], wrap(() => import('@/features/manage/DocsPage')), ['owner', 'admin']), handle: { crumb: 'Documentos' } },
      { path: 'manage/reviews', element: roleGate(['assoc'], wrap(() => import('@/features/manage/ReviewsPage'))), handle: { crumb: 'Avaliações' } },
      { path: 'manage/sales', element: roleGate(['assoc'], wrap(() => import('@/features/manage/SalesPage')), ['owner', 'admin']), handle: { crumb: 'Vendas' } },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
